<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/core/CmprFormHint';
import '@campact/camper/cta/CmprPrefilledArea';
import '@campact/camper/icons/CmprIconSolidLock';
import CmprCheckbox from '@cta/frontend/components/CmprCheckbox.vue';
import CmprRadio from '@cta/frontend/components/CmprRadio.vue';

import { computed, inject, useId } from 'vue';
import StepItem from '@cta/donate_call_component/components/StepItem.vue';

const store = inject('store');

const props = defineProps({
  answerOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  answerAllowMultiple: {
    type: Boolean,
    default: false,
  },
  answerTitles: {
    type: Object,
    default() {
      return {
        default: undefined,
        valid: undefined,
      };
    },
  },
  i18n: {
    type: Object,
    default: undefined,
  },
});

const emit = defineEmits(['try-open', 'commit']);

const answerStepTitle = computed(() => (
  store.values.answers.length > 0 ? props.answerTitles.valid : props.answerTitles.default
));

const answerSummaryTitle = computed(() => (
  Array.isArray(store.values.answers) ? store.values.answers.join(', ') : store.values.answers
));

function commitWithTimeout() {
  setTimeout(() => emit('commit'));
}

const id = useId();
</script>

<template>
  <div class="answer-step">
    <StepItem
      :number="1"
      :opened="store.answerStepOpen"
      :title="answerStepTitle"
      :tls-notice="i18n.tlsNotice"
      @try-open="emit('try-open')"
    >
      <template #opened>
        <slot />

        <div
          v-if="answerAllowMultiple"
          class="answer-step__answers"
        >
          <div
            v-for="({value, hint}, index) in answerOptions"
            :key="value"
            class="cmpr-checkbox-control answer"
            :class="{'answer--selected': store.values.answers.includes(value)}"
          >
            <CmprCheckbox
              :id="`${id}-${index}`"
              v-model="store.values.answers"
              data-analytics-context="answer-step"
              data-analytics-id="form-call-form-answer-checkbox-${index + 1}"
              :data-analytics-inner-text="value"
              :value="value"
            />
            <label
              class="cmpr-checkbox-control__label"
              :for="`${id}-${index}`"
            >
              <div class="cmpr-checkbox-control__caption answer-title fw-black">{{ value }}</div>
              <div class="cmpr-checkbox-control__hint answer-hint">{{ hint }}</div>
            </label>
          </div>

          <cmpr-form-hint
            v-if="store.answerErrorUnlocked && store.errors.answers"
            variant="error"
          >
            {{ store.errors.answers }}
          </cmpr-form-hint>

          <cmpr-button
            data-analytics-context="answer-step"
            data-analytics-id="form-call-form-answer-commit-button"
            size="small"
            variant="secondary"
            @click="emit('commit')"
          >
            {{ i18n.goToAddressData }}
          </cmpr-button>
        </div>

        <div
          v-else
          class="answer-step__answers"
        >
          <div
            v-for="({value, hint}, index) in answerOptions"
            :key="value"
            class="cmpr-radio-control answer"
            :class="{'answer--selected': store.values.answers.includes(value)}"
          >
            <CmprRadio
              :id="`${id}-${index}`"
              v-model="store.values.answers"
              data-analytics-context="answer-step"
              data-analytics-id="form-call-form-answer-radio-${index + 1}"
              data-analytics-inner-text="answer"
              :value="value"
              @click="commitWithTimeout"
            />
            <label
              class="cmpr-radio-control__label"
              :for="`${id}-${index}`"
            >
              <div class="cmpr-radio-control__caption answer-title fw-black">{{ value }}</div>
              <div class="cmpr-radio-control__hint answer-hint">{{ hint }}</div>
            </label>
          </div>

          <cmpr-form-hint
            v-if="store.answerErrorUnlocked && store.errors.answers"
            variant="error"
          >
            {{ store.errors.answers }}
          </cmpr-form-hint>
        </div>
      </template>

      <template #closed>
        <cmpr-prefilled-area
          class="answer-step__summary"
          editable="editable"
          variant="success"
        >
          <div class="fw-bold">
            {{ answerSummaryTitle }}
          </div>
        </cmpr-prefilled-area>
      </template>
    </StepItem>
  </div>
</template>

<style scoped lang="scss">
.answer-step__answers {
  display: flex;
  flex-direction: column;
  gap: var(--cmpr-space-60);
}

.cmpr-checkbox-control,
.cmpr-radio-control {
  grid-template-columns: 1fr;

  &__input {
    grid-column: 1;
    grid-row: 1;
    margin: var(--cmpr-space-60);
    order: 1;
  }

  &__label {
    display: grid;
    gap: var(--cmpr-space-60) var(--cmpr-space-100);
    grid-column: 1;
    grid-row: 1;
    grid-template-columns: auto 1fr;
    padding: var(--cmpr-space-60);
  }

  &__caption {
    grid-column: 2;
  }

  &__hint {
    grid-column: span 2;
  }
}

.answer {
  background: var(--cmpr-color-white);
  border: var(--cmpr-border-width-20) solid var(--cmpr-color-neutral-30);
  border-radius: var(--cmpr-border-radius-20);
  color: var(--cmpr-color-text);

  &-title {
    color: var(--cmpr-color-text);
    font-size: var(--cmpr-font-size-base-30);
    font-weight: var(--cmpr-font-weight-30);
    line-height: var(--cmpr-font-line-height-40);
  }

  &-hint {
    color: var(--cmpr-color-text);
  }

  &--selected {
    background: var(--cmpr-color-secondary-10);
    border: var(--cmpr-border-width-20) solid var(--cmpr-color-secondary-80);

    .answer-title {
      color: var(--cmpr-color-secondary-80);
    }
  }
}
</style>
